/** Client Marathon API helpers. */

import { apiPath } from "../../lib/apiPath";

export type MarathonMetric = "score" | "time";
export type MarathonRewardKind = "none" | "dots" | "exclusive_item" | "shop_item" | "free_crate";

export type MarathonSegmentView = {
  id: string;
  sortOrder: number;
  modeId: string;
  modeLabel: string;
  metric: MarathonMetric;
  goalValue: number;
  levelIndex: number | null;
  goalLabel: string;
  rewardKind: Exclude<MarathonRewardKind, "none"> | null;
  rewardDots: number;
  rewardClaimed?: boolean;
  hasReward?: boolean;
};

export type MarathonAttempt = {
  token: string;
  segmentIndex: number;
  segmentId: string;
  modeId: string;
  metric: MarathonMetric;
  goalValue: number;
  modeLabel: string;
  levelIndex?: number | null;
  sequenceIds: string[];
};

export type MarathonLeaderboardRow = {
  rank: number;
  userId: string;
  displayName: string;
  imageUrl: string | null;
  segmentsCompleted: number;
  failSegmentIndex: number;
  failProgressRatio: number;
  rankingScore: number;
  segmentCountSnapshot: number;
  updatedAt: string;
};

export type MarathonStatusResponse = {
  segments: MarathonSegmentView[];
  segmentCount: number;
  leaderboard: MarathonLeaderboardRow[];
  myBest: {
    segmentsCompleted: number;
    failSegmentIndex: number;
    failProgressRatio: number;
    rankingScore: number;
    segmentCountSnapshot: number;
    rank: number | null;
  } | null;
  active: MarathonAttempt | null;
  signedIn: boolean;
};

export type AdminMarathonSegment = MarathonSegmentView & {
  rewardKind: MarathonRewardKind;
  rewardCosmeticId: string | null;
  rewardCrateId: string | null;
  createdAt: string;
  updatedAt: string;
};

async function parseJson(r: Response): Promise<Record<string, unknown>> {
  try {
    return (await r.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function fetchMarathonStatus(): Promise<MarathonStatusResponse | null> {
  const r = await fetch(apiPath("/api/marathon"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) return null;
  return (await r.json()) as MarathonStatusResponse;
}

export async function postMarathonStart(): Promise<{
  attempt: MarathonAttempt;
  segments: MarathonSegmentView[];
}> {
  const r = await fetch(apiPath("/api/marathon"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "start" }),
  });
  const data = await parseJson(r);
  if (!r.ok) throw Object.assign(new Error(String(data.error || "start_failed")), { status: r.status, data });
  return data as { attempt: MarathonAttempt; segments: MarathonSegmentView[] };
}

export async function postMarathonAdvance(
  token: string,
  progress: { score: number; timeSec: number },
): Promise<Record<string, unknown>> {
  const r = await fetch(apiPath("/api/marathon"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "advance", token, progress }),
  });
  const data = await parseJson(r);
  if (!r.ok) throw Object.assign(new Error(String(data.error || "advance_failed")), { status: r.status, data });
  return data;
}

export async function postMarathonFail(
  token: string,
  progress: {
    score: number;
    timeSec: number;
    segmentIndex?: number;
    failSegmentIndex?: number;
    segmentsCompleted?: number;
    failProgressRatio?: number;
  },
): Promise<Record<string, unknown>> {
  const r = await fetch(apiPath("/api/marathon"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "fail", token, progress }),
  });
  const data = await parseJson(r);
  if (!r.ok) throw Object.assign(new Error(String(data.error || "fail_failed")), { status: r.status, data });
  return data;
}

export async function postMarathonAbandon(): Promise<void> {
  await fetch(apiPath("/api/marathon"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ action: "abandon" }),
  });
}

export async function fetchAdminMarathonSegments(): Promise<AdminMarathonSegment[]> {
  const r = await fetch(apiPath("/api/admin-marathon"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await parseJson(r);
  if (!r.ok) throw new Error(String(data.error || "load_failed"));
  return Array.isArray(data.segments) ? (data.segments as AdminMarathonSegment[]) : [];
}

export async function fetchAdminMarathonShopItems(): Promise<Array<{ id: string; name: string; category: string }>> {
  const r = await fetch(apiPath("/api/admin-marathon?section=shop-items"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await parseJson(r);
  if (!r.ok) throw new Error(String(data.error || "load_failed"));
  return Array.isArray(data.items) ? (data.items as Array<{ id: string; name: string; category: string }>) : [];
}

export async function fetchAdminMarathonCosmetics(): Promise<
  Array<{ id: string; name: string; category: string; marathonOnly?: boolean }>
> {
  const r = await fetch(apiPath("/api/admin-marathon?section=cosmetics"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await parseJson(r);
  if (!r.ok) throw new Error(String(data.error || "load_failed"));
  return Array.isArray(data.items)
    ? (data.items as Array<{ id: string; name: string; category: string; marathonOnly?: boolean }>)
    : [];
}

export async function fetchAdminMarathonCrates(): Promise<Array<{ id: string; name: string }>> {
  const r = await fetch(apiPath("/api/admin-marathon?section=crates"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await parseJson(r);
  if (!r.ok) throw new Error(String(data.error || "load_failed"));
  return Array.isArray(data.crates) ? (data.crates as Array<{ id: string; name: string }>) : [];
}

export async function createAdminMarathonSegment(body: Record<string, unknown>): Promise<AdminMarathonSegment> {
  const r = await fetch(apiPath("/api/admin-marathon"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const data = await parseJson(r);
  if (!r.ok) throw new Error(String(data.error || "create_failed"));
  return data.segment as AdminMarathonSegment;
}

export async function updateAdminMarathonSegment(
  id: string,
  body: Record<string, unknown>,
): Promise<AdminMarathonSegment> {
  const r = await fetch(apiPath("/api/admin-marathon"), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ ...body, id }),
  });
  const data = await parseJson(r);
  if (!r.ok) throw new Error(String(data.error || "update_failed"));
  return data.segment as AdminMarathonSegment;
}

export async function deleteAdminMarathonSegment(id: string): Promise<void> {
  const r = await fetch(apiPath(`/api/admin-marathon?id=${encodeURIComponent(id)}`), {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) {
    const data = await parseJson(r);
    throw new Error(String(data.error || "delete_failed"));
  }
}

export async function reorderAdminMarathonSegments(order: string[]): Promise<AdminMarathonSegment[]> {
  const r = await fetch(apiPath("/api/admin-marathon"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ section: "reorder", order }),
  });
  const data = await parseJson(r);
  if (!r.ok) throw new Error(String(data.error || "reorder_failed"));
  return Array.isArray(data.segments) ? (data.segments as AdminMarathonSegment[]) : [];
}

export async function createAdminMarathonCosmetic(body: Record<string, unknown>): Promise<{
  id: string;
  name: string;
  category: string;
}> {
  const r = await fetch(apiPath("/api/admin-marathon"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ section: "cosmetic", ...body }),
  });
  const data = await parseJson(r);
  if (!r.ok) throw new Error(String(data.error || "cosmetic_failed"));
  const c = data.cosmetic as { id: string; name: string; category: string };
  return c;
}
